import type { ReactNode } from 'react';
import { useState } from 'react';
import { Badge, Banner, Box, Button, Group, Heading, Spinner, Switch, Text } from '@optiaxiom/react';
import { IconGlobe } from '@optiaxiom/icons';
import { useAppVersions, type AppVersionItem, type AppVersionState } from '@/lib/api';
import { formatVersionState, UNPUBLISHED_STATES } from './versionState';
import { formatDate } from './formatDate';
import styles from './AppDetailPage.module.css';

/** Ask for everything: the server excludes nothing when given no states. */
const NOTHING_EXCLUDED: readonly AppVersionState[] = [];

export function VersionsTab({ appId }: { appId: string }) {
  // Versions unpublished on every shard are excluded server-side (OCP-2203) so the tab isn't
  // buried under retired versions; this is the escape hatch back to the full list.
  const [showUnpublished, setShowUnpublished] = useState(false);
  const { data, isLoading, isError, isFetching, refetch } = useAppVersions(
    appId,
    showUnpublished ? NOTHING_EXCLUDED : UNPUBLISHED_STATES,
  );

  const unpublishedCount = data?.excludedCount ?? 0;

  const toggle = (showUnpublished || unpublishedCount > 0) && (
    <Group flexDirection="row" justifyContent="flex-end">
      <Switch checked={showUnpublished} onCheckedChange={setShowUnpublished}>
        {/* The count is only worth showing while they're hidden — once they're on screen it's
            redundant, and omitting it avoids a flash of "(0)" during the refetch. */}
        Show unpublished{!showUnpublished && ` (${unpublishedCount})`}
      </Switch>
    </Group>
  );

  // Every branch renders through here so the toggle can't become unreachable exactly when it
  // matters: an app whose versions are all unpublished falls into the empty state, and
  // flipping the toggle refetches under a new query key and so passes back through loading.
  const frame = (children: ReactNode) => (
    <Group flexDirection="column" gap="12" alignItems="stretch">
      {toggle}
      {children}
    </Group>
  );

  if (isLoading) {
    return frame(
      <Group flexDirection="column" alignItems="center" className={styles.stateContainer}>
        <Spinner />
        <Text color="fg.secondary">Loading versions…</Text>
      </Group>,
    );
  }

  if (isError) {
    return frame(
      <Group flexDirection="column" alignItems="center" className={styles.stateContainer}>
        <Heading level="2" fontWeight="700">
          Couldn&rsquo;t load versions
        </Heading>
        <Text color="fg.secondary">Something went wrong loading this app&rsquo;s versions.</Text>
        <Button appearance="primary" onClick={() => void refetch()} loading={isFetching} disabled={isFetching}>
          Try again
        </Button>
      </Group>,
    );
  }

  const degradedShards = data?.degradedShards ?? [];

  // A version is "live" when it's running on any shard. Surface running versions first,
  // keeping the server's latest-updated-first order within each group (sort is stable).
  const isVersionRunning = (version: AppVersionItem) =>
    Object.values(version.shards).some((shard) => shard.state === 'RUNNING');
  const items = [...(data?.items ?? [])].sort(
    (a, b) => Number(isVersionRunning(b)) - Number(isVersionRunning(a)),
  );

  if (items.length === 0) {
    return frame(
      <Group flexDirection="column" alignItems="center" className={styles.stateContainer}>
        <Heading level="2" fontWeight="700">
          {unpublishedCount > 0 ? 'No published versions' : 'No versions yet'}
        </Heading>
        <Text color="fg.secondary">
          {unpublishedCount > 0
            ? `This app has ${unpublishedCount} unpublished ${
                unpublishedCount === 1 ? 'version' : 'versions'
              } hidden. Turn on “Show unpublished” to see them.`
            : 'Versions appear here once you scaffold and publish your app with the OCP CLI.'}
        </Text>
      </Group>,
    );
  }

  return frame(
    <>
      {degradedShards.length > 0 && (
        <Banner intent="warning" rounded="lg">
          <Text>
            Some regions were unavailable ({degradedShards.join(', ')}); this list may be incomplete.
          </Text>
        </Banner>
      )}

      {items.map((version) => {
        const isRunning = isVersionRunning(version);
        return (
          <Box
            key={version.version}
            border="1"
            rounded="lg"
            p="16"
            className={isRunning ? styles.runningCard : styles.versionCard}
          >
            <Group flexDirection="row" justifyContent="space-between" alignItems="center" gap="12">
              <Badge intent="neutral" rounded="md" variant="subtle" fontFamily="mono" fontWeight="600" textTransform="none" fontSize="md">
                v{version.version}
              </Badge>
              <Text color="fg.secondary" fontSize="sm">
                {formatDate(version.latestUpdatedAt)}
              </Text>
            </Group>

            <Group flexDirection="row" alignItems="center" gap="6" flexWrap="wrap" mt="12">
              <Box asChild color="fg.tertiary">
                <span>
                  <IconGlobe size={14} />
                </span>
              </Box>
              <Text color="fg.secondary" fontSize="sm">
                Region:
              </Text>
              {Object.entries(version.shards).map(([shardId, shardState], index) => {
                const display = formatVersionState(shardState.state);
                const isLast = index === Object.keys(version.shards).length - 1;
                return (
                  <Group key={shardId} flexDirection="row" alignItems="center" gap="6">
                    <Text fontSize="sm" fontWeight="600" textTransform="uppercase">
                      {shardId}
                    </Text>
                    <Badge intent={display.intent} variant="subtle" fontSize="xs">
                      {display.label}
                    </Badge>
                    {!isLast && <Text color="fg.tertiary">/</Text>}
                  </Group>
                );
              })}
            </Group>
          </Box>
        );
      })}
    </>,
  );
}
